import { useIsMobileHook } from '@teliaee/telia-common-components';
import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { GridColumn, Grid, Heading, Separator, Spinner } from 'telia-front-react';
import { useStores } from '../stores/RootStore';
import { StatCategory } from '../stores/SecureNetStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { FetchStatus } from '../util/FetchStatus';
import { Count } from '../components/Count';

interface SecureInternetProps {
  headingLevel?: number;
}

const SecureInternet = (props: SecureInternetProps) => {
  const { translate } = useContext(TranslationsContext);
  const { headingLevel } = props;
  const {
    secureNetStore: { getSumOfCategories, getCategoryStats, getSecureNetStats, getDate, isLoaded, status },
  } = useStores();

  const { isMobile } = useIsMobileHook();

  useEffect(() => {
    getSecureNetStats();
  }, []);

  const renderStatItemNumber = (number: number) => (
    <GridColumn width={['md-2']}>
      <span className="h3 text-error">
        <Count value={number} />
      </span>
    </GridColumn>
  );

  const renderStatItemTitle = (text: string) => <GridColumn width={['xs-6', 'md-2']}>{text}</GridColumn>;

  const renderCardHeading = () => {
    return (
      <Grid>
        <GridColumn>
          <Heading level={headingLevel} className="h3">
            {translate('securenet.title')}
          </Heading>
          <Separator color="invisible" spacing="xxsmall" />
          <p className="text-small text-light">
            {translate('securenet.date')} {getDate()}
          </p>
        </GridColumn>
      </Grid>
    );
  };

  const renderStatItemMobileVersion = (text: string, number: number) => {
    return (
      <GridColumn width={['xs-6']}>
        {text}
        <Separator color="invisible" spacing="xxsmall" />
        <span className="h3 text-error">
          <Count value={number} />
        </span>
      </GridColumn>
    );
  };

  const renderStatItemTitlesRow = () => (
    <>
      {renderStatItemTitle(translate('securenet.total'))}
      {renderStatItemTitle(translate('securenet.text'))}
      {renderStatItemTitle(translate('securenet.stat.1'))}
      {renderStatItemTitle(translate('securenet.stat.2'))}
      {renderStatItemTitle(translate('securenet.stat.3'))}
      {renderStatItemTitle(translate('securenet.stat.4'))}
    </>
  );

  const renderStatItemNumbersRow = () => {
    return (
      <>
        {renderStatItemNumber(getSumOfCategories())}
        <GridColumn width={['md-2']}>
          <span className="h3 text-error">
            <>
              <Count value={85} />
              {translate('securenet.percent')}
            </>
          </span>
        </GridColumn>
        {renderStatItemNumber(getCategoryStats(StatCategory.MALICIOUS))}
        {renderStatItemNumber(getCategoryStats(StatCategory.SPAM))}
        {renderStatItemNumber(getCategoryStats(StatCategory.BOTNET))}
        {renderStatItemNumber(getCategoryStats(StatCategory.VIRUS))}
      </>
    );
  };

  const renderMobileView = () => {
    return (
      <Grid>
        {renderStatItemMobileVersion(translate('securenet.total'), getSumOfCategories())}
        <GridColumn width={['xs-6']}>
          {translate('securenet.text')}
          <Separator color="invisible" spacing="xxsmall" />
          <span className="h3 text-error">
            <>
              <Count value={85} />
              {translate('securenet.percent')}
            </>
          </span>
        </GridColumn>
        {renderStatItemMobileVersion(translate('securenet.stat.1'), getCategoryStats(StatCategory.MALICIOUS))}
        {renderStatItemMobileVersion(translate('securenet.stat.2'), getCategoryStats(StatCategory.SPAM))}
        {renderStatItemMobileVersion(translate('securenet.stat.3'), getCategoryStats(StatCategory.BOTNET))}
        {renderStatItemMobileVersion(translate('securenet.stat.4'), getCategoryStats(StatCategory.VIRUS))}
      </Grid>
    );
  };

  if (status === FetchStatus.ERROR) {
    return null;
  }

  return (
    <>
      {renderCardHeading()}
      <Separator color="invisible" spacing="small" />
      {isLoaded ? (
        isMobile ? (
          renderMobileView()
        ) : (
          <Grid align={['center-xs']} verticalGutter="small" element="ul">
            {renderStatItemTitlesRow()}
            {renderStatItemNumbersRow()}
          </Grid>
        )
      ) : (
        <Spinner />
      )}
    </>
  );
};

export default observer(SecureInternet);
